import React, { useEffect, useState, useMemo } from 'react';
import { MdChevronRight, MdChevronLeft } from 'react-icons/md';
import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Icon,
  Stack,
  Button,
  Box,
  Spinner,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useKisi } from 'contexts/KisiContext';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Card from 'components/card/Card';
import CButton from 'components/CButton';
import Pagination from 'components/Pagination';

export default function KisiLocks({ canCreate }) {
  const { getAllKisiLocks, syncKisiLocksData } = useKisi();

  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [globalFilter, setGlobalFilter] = useState('');
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    search: '',
    limit: 10,
    page: 1,
  });

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor('id', {
      header: () => <Text color="gray.400">ID</Text>,
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="md" fontWeight="500">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('name', {
      header: () => <Text color="gray.400">NAME</Text>,
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="md" fontWeight="500">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('location', {
      header: () => <Text color="gray.400">LOCATION</Text>,
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="md" fontWeight="500">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('online', {
      header: () => <Text color="gray.400">ONLINE</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('open', {
      header: () => <Text color="gray.400">OPEN</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('unlocked', {
      header: () => <Text color="gray.400">UNLOCKED</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
  ];

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    fetchKisiLocks();
  }, [filter.search, filter.limit, filter.page]);

  const fetchKisiLocks = async () => {
    setIsLoading(true);
    try {
      const data = await getAllKisiLocks({
        search: filter.search,
        limit: filter.limit,
        page: filter.page,
      });
      if (data && data.kisiLocks) {
        setData(data.kisiLocks);
        setTotalPages(data.totalPages || 0);
      } else {
        console.warn('No users found or data is undefined');
        setData([]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncKisiLockData = async () => {
    try {
      const data = await syncKisiLocksData();
      if (!data.error) {
        fetchKisiLocks();
      }
    } catch (error) {
      console.error('Failed to sync kisi lock data:', error);
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPageIndex,
    }));
    setFilter((prev) => ({
      ...prev,
      page: newPageIndex,
    }));
  };

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '55px' }}>
      <Card px="0px" minHeight="80vh">
        <Flex direction="column" w="100%" overflowX="auto">
          <Flex
            align="center"
            justify="space-between"
            w="100%"
            px="22px"
            mb="36px"
          >
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, search: value, page: 1 }))
              }
              placeholder="Search..."
            />
            {canCreate && (
              <Flex gap="16px">
                <CButton
                  colorScheme="blue"
                  variant="solid"
                  onClick={() => handleSyncKisiLockData()}
                >
                  Sync Kisi Lock
                </CButton>
              </Flex>
            )}
          </Flex>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderRadius="10px"
              overflow="hidden"
              minHeight="500px"
            >
              <Spinner color="#fa8f03" />
            </Box>
          ) : (
            <>
              <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                <Table variant="simple">
                  <Thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Tr key={headerGroup.id} bg="#fa8f03">
                        {headerGroup.headers.map((header) => (
                          <Th key={header.id} borderColor="#fa8f03">
                            {header.isPlaceholder ? null : (
                              <Flex
                                onClick={header.column.getToggleSortingHandler()}
                                cursor={
                                  header.column.getCanSort() ? 'pointer' : 'default'
                                }
                                justify="space-between"
                                align="center"
                                color="white"
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </Flex>
                            )}
                          </Th>
                        ))}
                      </Tr>
                    ))}
                  </Thead>
                  <Tbody>
                    {data.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <Td key={cell.id} fontSize="sm" borderColor="#fa8f03">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </Td>
                          ))}
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={columns.length} textAlign="center">
                          No locks found.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
              <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
            </>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return (
    <SearchBar
      value={value}
      onChange={(e) => setValue(e.target.value)}
      h="44px"
      w={{ lg: '390px' }}
      borderRadius="16px"
      {...props}
    />
  );
}
